import { Routes } from '@angular/router';
import { authGuard, SPECTRUM_HELP_ROUTES } from 'common';

export const routes: Routes = [
    {
        path: 'validate',
        loadComponent: () => import('./validate/validate.component').then(m => m.ValidateComponent)
    },
    // What a payment link opens. Outside the guard by definition — a parent
    // following one has no Spectrum session, which is why links exist at all.
    // Must stay above the wildcard, or it resolves to the staff dashboard and
    // bounces the parent to login.
    {
        path: 'pay/:token',
        loadComponent: () => import('./screens/public/pay-link.component').then(m => m.PayLinkComponent)
    },
    {
        path: '',
        canActivate: [authGuard],
        loadComponent: () => import('./layout/screens.component').then(m => m.ScreensComponent),
        children: [
            // The Help centre: this module's manual and Getting around Spectrum.
            ...SPECTRUM_HELP_ROUTES,
            { path: '', redirectTo: 'dashboard', pathMatch: 'full' },

            // Phase 1 — read parity
            {
                path: 'dashboard',
                loadComponent: () => import('./screens/dashboard/dashboard.component').then(m => m.DashboardComponent)
            },
            {
                path: 'students',
                loadComponent: () => import('./screens/students/students.component').then(m => m.StudentsComponent)
            },
            {
                path: 'students/:id',
                loadComponent: () => import('./screens/student-fee360/student-fee360.component').then(m => m.StudentFee360Component)
            },
            {
                path: 'reports',
                loadComponent: () => import('./screens/reports/reports-hub.component').then(m => m.ReportsHubComponent)
            },
            {
                path: 'reports/fee-summary',
                loadComponent: () => import('./screens/reports/fee-summary/fee-summary-report.component').then(m => m.FeeSummaryReportComponent)
            },
            {
                path: 'reports/dues',
                loadComponent: () => import('./screens/reports/dues/due-report.component').then(m => m.DueReportComponent)
            },
            {
                path: 'reports/collection-summary',
                loadComponent: () => import('./screens/reports/collection-summary/collection-summary.component').then(m => m.CollectionSummaryComponent)
            },
            {
                path: 'reports/by-cashier',
                loadComponent: () => import('./screens/reports/by-cashier/by-cashier-report.component').then(m => m.ByCashierReportComponent)
            },
            {
                path: 'reports/by-fee-head',
                loadComponent: () => import('./screens/reports/by-fee-head/by-fee-head-report.component').then(m => m.ByFeeHeadReportComponent)
            },
            {
                path: 'reports/concessions',
                loadComponent: () => import('./screens/reports/concessions/concession-report.component').then(m => m.ConcessionReportComponent)
            },
            {
                path: 'concession-approvals',
                loadComponent: () => import('./screens/concession-approvals/concession-approvals.component').then(m => m.ConcessionApprovalsComponent)
            },
            {
                path: 'reports/collection-register',
                loadComponent: () => import('./screens/reports/collection-register/collection-register.component').then(m => m.CollectionRegisterComponent)
            },
            {
                path: 'reports/clearance',
                loadComponent: () => import('./screens/reports/clearance/clearance-report.component').then(m => m.ClearanceReportComponent)
            },
            {
                path: 'reports/invoices',
                loadComponent: () => import('./screens/reports/invoices/invoice-search.component').then(m => m.InvoiceSearchComponent)
            },
            {
                path: 'reports/due-by-date',
                loadComponent: () => import('./screens/reports/due-by-date/due-by-date-report.component').then(m => m.DueByDateReportComponent)
            },
            {
                path: 'reports/view-payroll',
                loadComponent: () => import('./screens/reports/view-payroll/view-payroll-report.component').then(m => m.ViewPayrollReportComponent)
            },
            {
                path: 'reports/salary-payments',
                loadComponent: () => import('./screens/reports/salary-payments/salary-payments-report.component').then(m => m.SalaryPaymentsReportComponent)
            },
            {
                path: 'reports/bank-statement',
                loadComponent: () => import('./screens/reports/bank-statement/bank-statement-report.component').then(m => m.BankStatementReportComponent)
            },
            {
                path: 'reports/purchase-payments',
                loadComponent: () => import('./screens/reports/purchase-payments/purchase-payments-report.component').then(m => m.PurchasePaymentsReportComponent)
            },

            // Phase 2 — write parity
            {
                path: 'collect',
                loadComponent: () => import('./screens/collect/collect.component').then(m => m.CollectComponent)
            },
            {
                path: 'budget',
                loadComponent: () => import('./screens/budget/budget.component').then(m => m.BudgetComponent)
            },
            {
                path: 'obligations',
                loadComponent: () => import('./screens/obligations/obligations.component').then(m => m.ObligationsComponent)
            },
            {
                path: 'cashflow',
                loadComponent: () => import('./screens/cashflow/cashflow.component').then(m => m.CashflowComponent)
            },
            {
                path: 'banking',
                loadComponent: () => import('./screens/banking/banking.component').then(m => m.BankingComponent)
            },
            {
                path: 'expenses',
                loadComponent: () => import('./screens/expenses/expenses.component').then(m => m.ExpensesComponent),
                children: [
                    { path: '', redirectTo: 'entries', pathMatch: 'full' },
                    { path: 'entries', loadComponent: () => import('./screens/expenses/entries/expense-entries.component').then(m => m.ExpenseEntriesComponent) },
                    { path: 'purchases', loadComponent: () => import('./screens/expenses/purchases/purchases.component').then(m => m.PurchasesComponent) },
                ]
            },
            {
                path: 'payroll',
                loadComponent: () => import('./screens/payroll/payroll-list.component').then(m => m.PayrollListComponent)
            },
            {
                path: 'payroll/payout-accounts',
                loadComponent: () => import('./screens/payroll/bank-accounts/payout-accounts.component').then(m => m.PayoutAccountsComponent)
            },
            {
                path: 'payroll/generate',
                loadComponent: () => import('./screens/payroll/generate/generate-payroll.component').then(m => m.GeneratePayrollComponent)
            },
            {
                path: 'payroll/:id',
                loadComponent: () => import('./screens/payroll/salary/employee-salary.component').then(m => m.EmployeeSalaryComponent)
            },
            {
                path: 'settings',
                loadComponent: () => import('./screens/settings/settings.component').then(m => m.SettingsComponent),
                children: [
                    { path: '', redirectTo: 'fee-heads', pathMatch: 'full' },
                    { path: 'fee-heads', loadComponent: () => import('./screens/settings/fee-heads/fee-heads.component').then(m => m.FeeHeadsComponent) },
                    { path: 'class-fees', loadComponent: () => import('./screens/settings/class-fees/class-fees.component').then(m => m.ClassFeesComponent) },
                    { path: 'categories', loadComponent: () => import('./screens/settings/categories/fee-categories.component').then(m => m.FeeCategoriesComponent) },
                    { path: 'concessions', loadComponent: () => import('./screens/settings/concessions/concessions.component').then(m => m.ConcessionsComponent) },
                    { path: 'activities', loadComponent: () => import('./screens/settings/activities/activity-fees.component').then(m => m.ActivityFeesComponent) },
                    { path: 'receipt-templates', loadComponent: () => import('./screens/settings/receipt-templates/receipt-templates.component').then(m => m.ReceiptTemplatesComponent) },
                    { path: 'payslip-templates', loadComponent: () => import('./screens/settings/payslip-templates/payslip-templates.component').then(m => m.PayslipTemplatesComponent) },
                    { path: 'gateways', loadComponent: () => import('./screens/settings/gateways/gateways.component').then(m => m.GatewaysComponent) },
                    { path: 'fine-rules', loadComponent: () => import('./screens/settings/fine-rules/fine-rules.component').then(m => m.FineRulesComponent) },
                    { path: 'reminder-cadence', loadComponent: () => import('./screens/settings/reminder-cadence/reminder-cadence.component').then(m => m.ReminderCadenceComponent) },
                    { path: 'alerts', loadComponent: () => import('./screens/settings/alerts/expense-alerts.component').then(m => m.ExpenseAlertsComponent) },
                    { path: 'expense-categories', loadComponent: () => import('./screens/settings/expense-categories/expense-categories.component').then(m => m.ExpenseCategoriesComponent) },
                ]
            },

            // The receipt designer sits outside the settings shell on purpose: that
            // shell supplies a `.screen` and a page header to each of its
            // children, and the designer wants the whole content area — the band
            // list and the rendered preview both want every pixel of height.
            {
                path: 'payslip-templates/:id',
                loadComponent: () => import('./screens/settings/payslip-designer/payslip-designer.component')
                    .then(m => m.PayslipDesignerComponent),
                // Same reason as the receipt designer below: without this only the
                // back arrow asks before discarding an edit.
                canDeactivate: [
                    (component: { canLeave(): boolean | Promise<boolean> }) => component.canLeave()
                ]
            },
            {
                path: 'receipt-templates/:id',
                loadComponent: () => import('./screens/settings/receipt-designer/receipt-designer.component')
                    .then(m => m.ReceiptDesignerComponent),
                // Without this only the back arrow asks before discarding an edit —
                // a sidebar link or the browser's Back would throw the work away in
                // silence.
                // Typed structurally rather than by importing the component: naming
                // it here would pull the designer into the main bundle and undo the
                // lazy load it is registered with.
                canDeactivate: [
                    (component: { canLeave(): boolean | Promise<boolean> }) => component.canLeave()
                ]
            },

            // Phase 3 — new capabilities
            {
                path: 'defaulters',
                loadComponent: () => import('./screens/defaulters/defaulters.component').then(m => m.DefaultersComponent)
            },
        ]
    },
    { path: '**', redirectTo: 'dashboard' }
];
